import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Input';
import { StatusDot } from '../../components/ui/StatusDot';
import { PaginaCrud, type CampoFormulario } from '../../components/layout/PaginaCrud';
import type { FilaInventario } from '../../../data/repositories/RepositorioInventario';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { repositorioSensores } from '../../../data/repositories/RepositorioSensores';
import { aNumero, aTexto } from '../../../data/mappers/conversiones';
import estilos from '../../components/layout/PaginaCrud.module.css';

interface Nodo {
  id: number;
  nombre: string;
  chipId: string | null;
  /** Credencial con la que el equipo envía sus mediciones. */
  token: string | null;
  estado: string;
  areaId: number | null;
  areaNombre: string;
  clienteId: number | null;
  clienteNombre: string;
  /** Conexión según el latido; `null` si el nodo no lo reporta. */
  enLinea: boolean | null;
}

export function Nodos() {
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [areaId, setAreaId] = useState<number | null>(null);

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const areasFiltro = useQuery({
    queryKey: ['catalogo', 'areas', clienteId],
    queryFn: () => repositorioCatalogos.areasDeCliente(clienteId),
    enabled: clienteId !== null,
    staleTime: 5 * 60_000,
  });

  /** Todas las áreas: al crear un nodo hay que poder elegir cualquiera. */
  const todasLasAreas = useQuery({
    queryKey: ['catalogo', 'areas', 'con-cliente'],
    queryFn: () => repositorioCatalogos.areasConCliente(),
    staleTime: 5 * 60_000,
  });

  const clienteConsultas = useQueryClient();

  const presencia = useQuery({
    queryKey: ['presencia-nodos', 'inventario'],
    queryFn: async () => {
      const nodos = await repositorioCatalogos.todosLosNodos();
      return repositorioSensores.presenciaDeNodos(nodos.map((n) => n.id));
    },
    refetchInterval: 30_000,
  });

  const mapear = useCallback(
    (fila: FilaInventario): Nodo => {
      const id = aNumero(fila.nodo_id) ?? 0;

      return {
        id,
        nombre: aTexto(fila.nodo_nombre) ?? `Nodo ${id}`,
        chipId: aTexto(fila.chip_id),
        token: aTexto(fila.token_envio),
        estado: aTexto(fila.nodo_estado) ?? 'activo',
        areaId: aNumero(fila.area_id),
        areaNombre: aTexto(fila.area_nombre) ?? '—',
        clienteId: aNumero(fila.cliente_id),
        clienteNombre: aTexto(fila.cliente_nombre) ?? '—',
        enLinea: presencia.data?.get(id)?.enLinea ?? null,
      };
    },
    [presencia.data],
  );

  /** Vigilancia de conexión guardada para ese nodo, si ya se conoce. */
  const presenciaDeFila = (fila: FilaInventario) => presencia.data?.get(aNumero(fila.nodo_id) ?? 0);

  /*
   * Nueve campos en tres filas de tres, agrupados por lo que se responde en
   * cada una: qué es y de quién (nombre, cliente, área), con qué credenciales
   * habla (chip, token, estado) y cómo se le vigila (ubicación, vigilancia,
   * plazo). El orden del array es el orden en pantalla.
   */
  const campos: CampoFormulario[] = useMemo(
    () => [
      {
        clave: 'nombre',
        etiqueta: 'Nombre',
        tipo: 'texto',
        requerido: true,
        placeholder: 'Portón principal, Sala de bombas…',
      },
      /*
       * El cliente no se guarda en `nodos` —se deduce del área—, pero se pide
       * primero porque es como se piensa al dar de alta un equipo, y porque
       * deja la lista de áreas en las de ese cliente en vez de en todas.
       */
      {
        clave: 'cliente_id',
        etiqueta: 'Cliente',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un cliente',
        opciones: (clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre })),
      },
      {
        clave: 'area_id',
        etiqueta: 'Área',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un área',
        ayuda: 'Dónde está instalado físicamente',
        opciones: (valores) => {
          const cliente = valores.cliente_id ? Number(valores.cliente_id) : null;
          return (todasLasAreas.data ?? [])
            .filter((a) => cliente === null || a.clienteId === cliente)
            .map((a) => ({ valor: a.id, etiqueta: a.nombre }));
        },
      },
      {
        clave: 'chip_id',
        etiqueta: 'Chip ID',
        tipo: 'texto',
        largoMaximo: 8,
        placeholder: 'Se rellena al dar de alta el hardware',
        ayuda:
          'Código grabado en el dispositivo; debe coincidir con el que envía. Máximo 8 caracteres',
      },
      {
        clave: 'token_envio',
        etiqueta: 'Token de envío',
        tipo: 'texto',
        requerido: true,
        largoMaximo: 16,
        placeholder: 'El que lleve grabado el equipo',
        ayuda:
          'Contraseña del equipo: sin ella no puede enviar mediciones. Debe ser distinta en cada nodo',
      },
      {
        clave: 'estado',
        etiqueta: 'Estado',
        tipo: 'select',
        requerido: true,
        inicial: 'activo',
        opciones: [
          { valor: 'activo', etiqueta: 'Activo' },
          { valor: 'inactivo', etiqueta: 'Inactivo' },
        ],
      },
      { clave: 'ubicacion', etiqueta: 'Ubicación', tipo: 'texto', placeholder: 'Opcional' },
      /*
       * Vigilancia de conexión. No se guarda en `nodos` sino en
       * `nodos_runtime`, así que la escribe `alGuardar` una vez creado el nodo.
       *
       * Apagada, el nodo nunca aparece como caído: es lo correcto en equipos
       * que sólo hablan cuando tienen algo que decir. Encendida, el servidor
       * lo da por desconectado si pasa el plazo sin dar señal.
       */
      {
        clave: 'hb_activo',
        etiqueta: 'Vigilar la conexión',
        tipo: 'select',
        ayuda: 'Avisa cuando el equipo deja de dar señal de vida',
        opciones: [
          { valor: 'false', etiqueta: 'No vigilar' },
          { valor: 'true', etiqueta: 'Vigilar' },
        ],
      },
      {
        clave: 'hb_segundos',
        etiqueta: 'Señal de vida cada',
        tipo: 'numero',
        placeholder: '300',
        ayuda:
          'Segundos. Debe ser mayor que el intervalo con el que el equipo envía: si es más corto, se dará por caído entre envío y envío',
      },
    ],
    [todasLasAreas.data, clientes.data],
  );

  return (
    <PaginaCrud<Nodo>
      entidad="nodos"
      titulo="Nodos"
      descripcion="Equipos instalados en campo que agrupan sensores y actuadores."
      textoVacio="Da de alta un nodo para empezar a recibir mediciones."
      mapear={mapear}
      claveFila={(n) => n.id}
      textoBusqueda={(n) =>
        `${n.nombre} ${n.chipId ?? ''} ${n.token ?? ''} ${n.areaNombre} ${n.clienteNombre}`
      }
      idDeFila={(fila) => aNumero(fila.nodo_id) ?? 0}
      campos={campos}
      filtrosConsulta={{ cliente_id: clienteId, area_id: areaId }}
      filtrosExtra={
        <>
          <Select
            etiqueta="Cliente"
            placeholder="Todos los clientes"
            value={clienteId ?? ''}
            onChange={(e) => {
              setClienteId(e.target.value ? Number(e.target.value) : null);
              setAreaId(null);
            }}
            opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
          />
          <Select
            etiqueta="Área"
            placeholder={clienteId ? 'Todas las áreas' : 'Elige un cliente'}
            disabled={!clienteId}
            value={areaId ?? ''}
            onChange={(e) => {
              setAreaId(e.target.value ? Number(e.target.value) : null);
            }}
            opciones={(areasFiltro.data ?? []).map((a) => ({ valor: a.id, etiqueta: a.nombre }))}
          />
        </>
      }
      aFormulario={(fila) => {
        const presencia = presenciaDeFila(fila);

        return {
          nombre: aTexto(fila.nodo_nombre) ?? '',
          cliente_id: aNumero(fila.cliente_id) ?? '',
          area_id: aNumero(fila.area_id) ?? '',
          chip_id: aTexto(fila.chip_id) ?? '',
          token_envio: aTexto(fila.token_envio) ?? '',
          ubicacion: '',
          estado: aTexto(fila.nodo_estado) ?? 'activo',
          hb_activo: String(presencia?.vigilaConexion ?? false),
          hb_segundos: presencia?.cadaSegundos ?? 300,
        };
      }}
      validar={(v) => {
        // Sin token el equipo no puede enviar nada. La base lo admite vacío,
        // así que si no se comprueba aquí se crean nodos mudos que parecen
        // correctos hasta que alguien se pregunta por qué no llega ningún dato.
        if (!String(v.token_envio ?? '').trim()) {
          return 'El token de envío es obligatorio: sin él el equipo no puede enviar mediciones.';
        }
        return null;
      }}
      aRegistro={(v) => ({
        nombre: String(v.nombre ?? '').trim(),
        // `cliente_id` sólo sirve para acotar el desplegable de áreas: la tabla
        // `nodos` no lo guarda, lo deduce del área. Por eso no se envía.
        area_id: Number(v.area_id),
        chip_id: String(v.chip_id ?? '').trim() || null,
        token_envio: String(v.token_envio ?? '').trim(),
        ubicacion: String(v.ubicacion ?? '').trim() || null,
        estado: v.estado,
      })}
      alGuardar={async (id, valores) => {
        await repositorioSensores.configurarLatido(
          Number(id),
          valores.hb_activo === 'true',
          Number(valores.hb_segundos) || 300,
        );
        void clienteConsultas.invalidateQueries({ queryKey: ['presencia-nodos'] });
      }}
      textoBorrado={(n) =>
        `¿Eliminar el nodo «${n.nombre}»?\n\n` +
        'Sus sensores, actuadores y todo su historial de mediciones se eliminarán con él.'
      }
      columnas={[
        {
          clave: 'nombre',
          encabezado: 'Nodo',
          movil: 'titulo',
          render: (n) => <span className={estilos.nombre}>{n.nombre}</span>,
        },
        {
          clave: 'ubicacion',
          encabezado: 'Ubicación',
          movil: 'subtitulo',
          render: (n) => (
            <span className={estilos.secundario}>
              {n.clienteNombre} · {n.areaNombre}
            </span>
          ),
        },
        {
          clave: 'chip',
          encabezado: 'Chip ID',
          render: (n) => <span className={estilos.identificador}>{n.chipId ?? '—'}</span>,
        },
        /*
         * El token con el que el equipo se identifica al enviar. Va
         * monoespaciado porque se transcribe al firmware y ahí un 0 y una O no
         * pueden confundirse; un clic lo selecciona entero para copiarlo.
         */
        {
          clave: 'token',
          encabezado: 'Token de envío',
          render: (n) =>
            n.token ? (
              <span className={estilos.identificador} title="Credencial del equipo">
                {n.token}
              </span>
            ) : (
              <Badge color="var(--status-warning)">Sin token</Badge>
            ),
        },
        {
          clave: 'conexion',
          encabezado: 'Conexión',
          movil: 'detalle',
          render: (n) =>
            n.enLinea === null ? (
              <Badge
                color="var(--text-3)"
                title="Este nodo no tiene vigilancia de conexión activada"
              >
                Sin vigilar
              </Badge>
            ) : (
              <StatusDot
                color={n.enLinea ? 'var(--status-success)' : 'var(--status-danger)'}
                encendido
                texto={n.enLinea ? 'Conectado' : 'Desconectado'}
                tamano={8}
              />
            ),
        },
        {
          clave: 'estado',
          encabezado: 'Estado',
          movil: 'detalle',
          render: (n) => (
            <Badge color={n.estado === 'activo' ? 'var(--status-success)' : 'var(--text-3)'}>
              {n.estado}
            </Badge>
          ),
        },
      ]}
    />
  );
}
